import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronUp,
  Bath,
  UtensilsCrossed,
  LayoutGrid,
  Store,
  CheckCircle,
  Clock,
  Award,
  Shield,
  Users,
  Banknote,
  ClipboardList,
  Palette,
  Hammer,
  Sparkles,
  Star,
  ArrowRight,
  Facebook
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Business Info
const BUSINESS = {
  name: "Tims Tilers",
  phone: "07930 960142",
  phoneLink: "tel:07930960142",
  email: "info@timstilers.co.uk",
  address: "5 Houghton Ln, Sancton, East Yorkshire, YO43 4QU",
  experience: "25+",
  logo: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/si46mrw2_logo.jpg",
  facebook: "https://www.facebook.com/Timstilers.darrencronshaw"
};

// Showroom Images
const SHOWROOM_IMAGES = [
  "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/irc8uwa9_showroom.jpeg",
  "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/yvdh0r3l_office%202.jpg"
];

// Gallery Images
const GALLERY_IMAGES = [
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/igmrvsfv_11.jpg", 
    alt: "Modern grey bathroom with shower and bath",
    category: "Bathroom"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/gwn4d8fe_9.jpg", 
    alt: "Kitchen floor tiling with light stone effect tiles",
    category: "Kitchen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/h5ik3a96_10.jpg", 
    alt: "Bathroom with walk-in shower enclosure",
    category: "Bathroom"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/o1auqi0m_7.jpg", 
    alt: "Tiled bathtub surround with checkered floor",
    category: "Bathroom"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/wewhrjqe_12.jpg", 
    alt: "Grey tiled bathroom with wall hung WC",
    category: "Bathroom"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/ri0vj1ey_1.jpg", 
    alt: "Modern WC bathroom tiling with textured wall tiles",
    category: "Bathroom"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/i3hs3hag_2.jpg", 
    alt: "Kitchen floor tiling with natural stone effect",
    category: "Kitchen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/9z7zseh0_3.jpg", 
    alt: "Luxury bathroom with LED mirror and grey tiles",
    category: "Bathroom"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/60mrdsyr_6.jpg", 
    alt: "Precision shower niche with chrome trim",
    category: "Detail"
  }
];

// Video URLs
const PROJECT_VIDEOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/1o3cw3ee_v1.mp4",
    title: "Bathroom Tiling Project"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/gkr9znr2_v2.mp4",
    title: "Tiling Transformation"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/li30s38i_v3.mp4",
    title: "Professional Finish"
  }
];

// Services Data
const SERVICES = [
  {
    icon: Bath,
    title: "Bathroom Tiling",
    description: "Professional wall and floor tiling for bathrooms and wet rooms. Expert installation for lasting results."
  },
  {
    icon: UtensilsCrossed,
    title: "Kitchen Tiling",
    description: "Stylish splashbacks and floor tiling for modern kitchens. Transform your cooking space."
  },
  {
    icon: LayoutGrid,
    title: "Floor Tiling",
    description: "Durable and precise floor tiling for homes and commercial spaces. Built to withstand daily use."
  },
  {
    icon: Store,
    title: "Tile Supply",
    description: "Access to our showroom to choose the right tiles for your project. Expert advice included."
  }
];

// Why Choose Us Data
const FEATURES = [
  { icon: Award, title: "Over 25 Years Experience" },
  { icon: Shield, title: "Professional Tiling Specialists" },
  { icon: Bath, title: "Kitchen & Bathroom Experts" },
  { icon: Store, title: "Tile Supply & Showroom" },
  { icon: Users, title: "Reliable Local Service" },
  { icon: Banknote, title: "Competitive Pricing" }
];

// Process Steps
const PROCESS_STEPS = [
  { icon: ClipboardList, title: "Consultation", description: "Free quote and project discussion" },
  { icon: Palette, title: "Tile Selection", description: "Choose tiles at our showroom" },
  { icon: Hammer, title: "Installation", description: "Professional fitting by experts" },
  { icon: Sparkles, title: "Finishing", description: "Quality check and clean up" }
];

// Testimonials
const TESTIMONIALS = [
  {
    name: "Sarah M.",
    location: "York",
    rating: 5,
    text: "Absolutely fantastic work on our bathroom renovation. Tim and his team were professional, tidy, and the finish is stunning. Highly recommend!"
  },
  {
    name: "James P.",
    location: "Beverley",
    rating: 5,
    text: "Used Tims Tilers for our kitchen floor and splashback. The attention to detail is incredible. Great communication throughout the project."
  },
  {
    name: "Linda H.",
    location: "Market Weighton",
    rating: 5,
    text: "Third time using Tim for tiling work. Consistent quality every time. The showroom helped us pick the perfect tiles for our en-suite."
  }
];

// Navigation Component
function Navigation({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) {
  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#showroom", label: "Showroom" },
    { href: "#gallery", label: "Gallery" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'header-sticky shadow-lg py-2' : 'bg-white py-4'
      }`}
      data-testid="main-header"
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center group" data-testid="logo-link">
            <img 
              src={BUSINESS.logo} 
              alt={BUSINESS.name} 
              className={`transition-all duration-300 ${isScrolled ? 'h-14' : 'h-16'}`}
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative font-semibold text-sm tracking-wider text-slate-700 hover:text-[#4a90c2] transition-colors duration-300 py-2 group"
              >
                {link.label.toUpperCase()}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c41e2a] transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <a 
              href={BUSINESS.phoneLink}
              className="flex items-center gap-2 font-bold text-slate-800 hover:text-[#4a90c2] transition-colors"
              data-testid="header-phone"
            >
              <div className="w-10 h-10 rounded-full bg-[#4a90c2]/10 flex items-center justify-center">
                <Phone className="h-4 w-4 text-[#4a90c2]" />
              </div>
              <span>{BUSINESS.phone}</span>
            </a>
            <a href="#contact">
              <Button className="btn-primary rounded-lg" data-testid="header-quote-btn">
                Get Quote
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-slate-900" />
            ) : (
              <Menu className="h-6 w-6 text-slate-900" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu ${mobileMenuOpen ? 'open' : ''} z-[60]`}
        data-testid="mobile-menu"
      >
        <div className="p-8 h-full flex flex-col">
          <div className="flex justify-between items-center mb-10">
            <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-14" />
            <button 
              onClick={() => setMobileMenuOpen(false)} 
              aria-label="Close menu"
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="h-6 w-6 text-slate-900" />
            </button>
          </div>
          <nav className="flex flex-col gap-2 flex-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-semibold text-lg tracking-wider text-slate-900 hover:text-[#4a90c2] py-3 px-4 rounded-lg hover:bg-slate-50 transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="pt-8 border-t border-slate-200 space-y-4">
            <a 
              href={BUSINESS.phoneLink}
              className="flex items-center gap-3 font-bold text-slate-900"
            >
              <div className="w-12 h-12 rounded-full bg-[#4a90c2] flex items-center justify-center">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Call us</p>
                <p className="text-lg">{BUSINESS.phone}</p>
              </div>
            </a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
              <Button className="btn-primary w-full rounded-lg">Get Free Quote</Button>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </header>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Video */}
      <div className="absolute inset-0">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="w-full h-full object-cover"
          poster="https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/igmrvsfv_11.jpg"
        >
          <source src="https://customer-assets.emergentagent.com/job_tiling-eastyorkshire/artifacts/1o3cw3ee_v1.mp4" type="video/mp4" />
        </video>
      </div>
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      
      {/* Animated shapes */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[#4a90c2]/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#c41e2a]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      
      <div className="container mx-auto px-4 md:px-8 relative z-10 pt-24">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8">
            <span className="w-2 h-2 bg-[#f5c842] rounded-full animate-pulse"></span>
            <span className="text-white/90 text-sm font-medium tracking-wider uppercase">
              Professional Tiling Services
            </span>
          </div>
          
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight leading-[1.05] mb-8"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="hero-headline"
          >
            Expert Tiling Services in{' '}
            <span className="text-[#f5c842]">East Yorkshire</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-12 max-w-2xl">
            Over 25 years of experience supplying and fitting high-quality tiles for kitchens, 
            bathrooms and homes. Quality craftsmanship you can trust.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <a href="#contact">
              <Button className="btn-primary text-base px-10 py-5 rounded-lg" data-testid="hero-quote-btn">
                Request a Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a href={BUSINESS.phoneLink}>
              <Button className="btn-secondary text-base px-10 py-5 rounded-lg border-white/30 text-white hover:bg-white hover:text-[#0d2137]" data-testid="hero-call-btn">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
          </div>
          
          {/* Stats row */}
          <div className="flex flex-wrap gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>25+</p>
              <p className="text-white/60 text-sm uppercase tracking-wider">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>500+</p>
              <p className="text-white/60 text-sm uppercase tracking-wider">Projects Done</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>100%</p>
              <p className="text-white/60 text-sm uppercase tracking-wider">Satisfaction</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-white/50 text-xs uppercase tracking-[0.2em]">Scroll</span>
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/80 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection() {
  return (
    <section id="about" className="py-24 md:py-32 bg-white" data-testid="about-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Content */}
          <div>
            <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">About Us</p>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-8 leading-tight"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Trusted Tiling Experts with 25+ Years Experience
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Tims Tilers has built a reputation for excellence across East Yorkshire. 
              We combine traditional craftsmanship with modern techniques to deliver 
              exceptional results on every project.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              From contemporary bathrooms to classic kitchen splashbacks, our team delivers 
              precision tiling that stands the test of time. Visit our showroom to explore 
              our extensive tile collection.
            </p>
            
            {/* Highlights */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Award, text: "25+ Years Experience" },
                { icon: Bath, text: "Bathroom Specialists" },
                { icon: Store, text: "Tile Showroom" },
                { icon: Shield, text: "Quality Guaranteed" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4a90c2] to-[#3a7bb0] flex items-center justify-center flex-shrink-0 shadow-lg">
                    <item.icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-slate-800 font-semibold">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-7 space-y-4">
                <img 
                  src={GALLERY_IMAGES[0].src} 
                  alt={GALLERY_IMAGES[0].alt}
                  className="w-full h-56 object-cover rounded-2xl shadow-xl"
                />
                <img 
                  src={GALLERY_IMAGES[2].src} 
                  alt={GALLERY_IMAGES[2].alt}
                  className="w-full h-72 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <div className="col-span-5 space-y-4 pt-12">
                <img 
                  src={GALLERY_IMAGES[1].src} 
                  alt={GALLERY_IMAGES[1].alt}
                  className="w-full h-72 object-cover rounded-2xl shadow-xl"
                />
                <img 
                  src={GALLERY_IMAGES[4].src} 
                  alt={GALLERY_IMAGES[4].alt}
                  className="w-full h-56 object-cover rounded-2xl shadow-xl"
                />
              </div>
            </div>
            {/* Experience badge */}
            <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#c41e2a] to-[#a31822] text-white p-6 rounded-2xl shadow-2xl">
              <p className="text-5xl font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>25+</p>
              <p className="text-sm uppercase tracking-wider text-white/80">Years Experience</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  return (
    <section id="services" className="py-24 md:py-32 bg-slate-50" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">Our Services</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Professional Tiling Solutions
          </h2>
          <p className="text-lg text-slate-600">
            From bathroom renovations to kitchen splashbacks, we deliver exceptional tiling solutions tailored to your needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {SERVICES.map((service, index) => (
            <div 
              key={index}
              className="service-card p-8 rounded-2xl group"
              data-testid={`service-card-${index}`}
            >
              <div className="feature-icon mb-8 group-hover:scale-110 transition-transform duration-500">
                <service.icon className="h-7 w-7" />
              </div>
              <h3 
                className="text-xl font-bold text-[#0d2137] uppercase mb-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {service.title}
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                {service.description}
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 text-[#4a90c2] font-semibold text-sm uppercase tracking-wider hover:text-[#c41e2a] transition-colors group/link">
                Get Quote 
                <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Gallery Section
function GallerySection({ setLightboxImage }) {
  return (
    <section id="gallery" className="py-24 md:py-32 bg-slate-50" data-testid="gallery-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">Our Portfolio</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Recent Projects
          </h2>
          <p className="text-lg text-slate-600">
            Browse our portfolio of completed tiling projects across East Yorkshire.
          </p>
        </div>

        <div className="masonry-grid">
          {GALLERY_IMAGES.map((image, index) => (
            <div 
              key={index}
              className="masonry-item gallery-item relative cursor-pointer group"
              onClick={() => setLightboxImage(image.src)}
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                className="w-full h-auto rounded-xl"
                loading="lazy"
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 rounded-xl">
                <span className="text-white font-bold uppercase tracking-wider text-sm mb-1">
                  {image.category}
                </span>
                <span className="text-white/70 text-sm">Click to view</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Showroom Section
function ShowroomSection() {
  return (
    <section id="showroom" className="py-24 md:py-32 bg-white overflow-hidden" data-testid="showroom-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <img 
              src={SHOWROOM_IMAGES[0]} 
              alt="Tims Tilers Showroom - Browse our tile selection"
              className="w-full h-[450px] object-cover rounded-2xl shadow-2xl"
            />
            {/* Decorative element */}
            <div className="absolute -z-10 -bottom-10 -left-10 w-72 h-72 bg-[#4a90c2]/10 rounded-full blur-3xl"></div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-[#f5c842]/20 rounded-full px-4 py-2 mb-6">
              <Store className="h-4 w-4 text-[#c41e2a]" />
              <span className="text-[#0d2137] text-sm font-semibold uppercase tracking-wider">Tile Showroom</span>
            </div>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6 leading-tight"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Visit Our Tile Showroom
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Browse our extensive collection of tiles in person at our dedicated showroom. 
              We stock a wide range of styles, colours and finishes to suit every project and budget.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              Our team is on hand to provide expert advice and help you choose the perfect tiles 
              for your bathroom, kitchen or any room in your home.
            </p>
            
            {/* Features */}
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {[
                "Wide range of tile styles",
                "Expert advice available",
                "Competitive trade prices",
                "Samples to take home"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#4a90c2] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>

            <a href="#contact">
              <Button className="btn-primary rounded-xl">
                Book a Showroom Visit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Video Section
function VideoSection() {
  const videoRefs = useRef([]);
  const [expandedVideo, setExpandedVideo] = useState(null);

  const handlePlay = (index) => {
    // Pause all other videos when one starts playing
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
      }
    });
  };

  const handleExpand = (index) => {
    // Pause all videos when expanding
    videoRefs.current.forEach((video) => {
      if (video) video.pause();
    });
    setExpandedVideo(index);
  };

  const handleCloseExpand = () => {
    setExpandedVideo(null);
  };

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="video-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">See Our Work</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Project Videos
          </h2>
          <p className="text-lg text-slate-600">
            Watch our tiling projects come to life - quality craftsmanship in action.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {PROJECT_VIDEOS.map((video, index) => (
            <div 
              key={index}
              className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-500"
              data-testid={`video-${index}`}
            >
              <video 
                ref={el => videoRefs.current[index] = el}
                className="w-full h-[500px] object-cover"
                controls
                preload="metadata"
                playsInline
                onPlay={() => handlePlay(index)}
              >
                <source src={video.src} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute top-3 right-3 z-10">
                <button
                  onClick={() => handleExpand(index)}
                  className="w-10 h-10 bg-white/90 hover:bg-white rounded-lg flex items-center justify-center shadow-lg transition-all hover:scale-110"
                  aria-label="Expand video"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d2137" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <polyline points="9 21 3 21 3 15"></polyline>
                    <line x1="21" y1="3" x2="14" y2="10"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                  </svg>
                </button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0d2137]/90 to-transparent pointer-events-none">
                <h3 className="text-white font-bold text-lg">{video.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Expanded Video Modal */}
      {expandedVideo !== null && (
        <div 
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
          onClick={handleCloseExpand}
        >
          <button
            onClick={handleCloseExpand}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <div 
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video 
              className="w-full max-h-[80vh] rounded-xl"
              controls
              autoPlay
              playsInline
            >
              <source src={PROJECT_VIDEOS[expandedVideo].src} type="video/mp4" />
            </video>
            <p className="text-white text-center mt-4 text-xl font-bold">{PROJECT_VIDEOS[expandedVideo].title}</p>
          </div>
        </div>
      )}
    </section>
  );
}

// Why Choose Us Section
function WhyChooseUsSection() {
  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-[#0d2137] via-[#1a3a5c] to-[#0d2137] relative overflow-hidden" data-testid="why-choose-section">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#4a90c2]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#c41e2a]/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[#f5c842] font-bold uppercase tracking-[0.2em] mb-4 text-sm">Why Choose Us</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            The Tims Tilers Difference
          </h2>
          <p className="text-lg text-white/70">
            We're committed to delivering exceptional quality and service on every project.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((feature, index) => (
            <div 
              key={index}
              className="group p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-[#f5c842]/50 transition-all duration-500"
              data-testid={`feature-${index}`}
            >
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-lg">
                <feature.icon className="h-7 w-7 text-[#4a90c2]" />
              </div>
              <h3 className="text-white font-bold text-xl mb-2">{feature.title}</h3>
              <p className="text-white/60 text-sm">Quality you can trust, every time.</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Process Section
function ProcessSection() {
  return (
    <section id="process" className="py-24 md:py-32 bg-white" data-testid="process-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">How It Works</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Our Simple Process
          </h2>
          <p className="text-lg text-slate-600">
            From initial consultation to final finish, we make the tiling process smooth and stress-free.
          </p>
        </div>

        <div className="relative">
          {/* Desktop Timeline Line */}
          <div className="hidden md:block process-line" />
          
          <div className="grid md:grid-cols-4 gap-8 lg:gap-12">
            {PROCESS_STEPS.map((step, index) => (
              <div key={index} className="relative text-center group" data-testid={`process-step-${index}`}>
                {/* Step Number */}
                <div className="relative z-10 w-20 h-20 mx-auto mb-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#c41e2a] to-[#a31822] rounded-2xl transform rotate-6 group-hover:rotate-12 transition-transform duration-500"></div>
                  <div className="relative w-full h-full bg-gradient-to-br from-[#c41e2a] to-[#a31822] rounded-2xl flex items-center justify-center shadow-xl">
                    <span 
                      className="text-3xl font-bold text-white"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      {index + 1}
                    </span>
                  </div>
                </div>
                <div className="bg-slate-50 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-500">
                  <step.icon className="h-10 w-10 text-[#4a90c2] mx-auto mb-5" />
                  <h3 
                    className="text-xl font-bold text-[#0d2137] uppercase mb-3"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection() {
  return (
    <section className="py-24 md:py-32 bg-slate-50" data-testid="testimonials-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">Testimonials</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            What Our Customers Say
          </h2>
          <p className="text-lg text-slate-600">
            Don't just take our word for it - hear from our satisfied customers.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((testimonial, index) => (
            <div 
              key={index}
              className="testimonial-card p-8 rounded-2xl"
              data-testid={`testimonial-${index}`}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i}
                    className={`h-5 w-5 ${i < testimonial.rating ? 'star-filled fill-current' : 'star-empty'}`}
                  />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-8 text-lg">
                "{testimonial.text}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4a90c2] to-[#3a7bb0] flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-[#0d2137] text-lg">{testimonial.name}</p>
                  <p className="text-slate-500">{testimonial.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_details: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/enquiries`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', phone: '', email: '', project_details: '' });
      }
    } catch (error) {
      toast.error("Failed to submit enquiry. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[#c41e2a] font-bold uppercase tracking-[0.2em] mb-4 text-sm">Contact Us</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2137] uppercase tracking-wide mb-6"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Get Your Free Quote
          </h2>
          <p className="text-lg text-slate-600">
            Ready to start your tiling project? Get in touch for a free, no-obligation quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3 bg-slate-50 p-8 md:p-12 rounded-3xl">
            <h3 
              className="text-2xl font-bold text-[#0d2137] uppercase mb-2"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Send Us an Enquiry
            </h3>
            <p className="text-slate-600 mb-8">Fill out the form and we'll get back to you within 24 hours.</p>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input w-full"
                  placeholder="John Smith"
                  required
                  data-testid="contact-name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number *</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input w-full"
                    placeholder="07123 456789"
                    required
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input w-full"
                    placeholder="john@example.com"
                    required
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Project Details *</label>
                <Textarea
                  value={formData.project_details}
                  onChange={(e) => setFormData({ ...formData, project_details: e.target.value })}
                  className="form-input w-full min-h-[160px]"
                  placeholder="Tell us about your tiling project - room type, approximate size, tile preferences..."
                  required
                  data-testid="contact-details"
                />
              </div>
              <Button 
                type="submit" 
                className="btn-primary w-full py-5 rounded-xl text-base"
                disabled={loading}
                data-testid="contact-submit"
              >
                {loading ? "Sending..." : "Send Enquiry"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Details */}
            <div className="bg-gradient-to-br from-[#0d2137] to-[#1a3a5c] p-8 md:p-10 rounded-3xl text-white">
              <h3 
                className="text-2xl font-bold uppercase mb-8"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Contact Information
              </h3>
              <div className="space-y-6">
                <a 
                  href={BUSINESS.phoneLink}
                  className="flex items-center gap-5 group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center group-hover:bg-[#f5c842] transition-colors">
                    <Phone className="h-6 w-6 text-white group-hover:text-[#0d2137]" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60 uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-xl font-bold group-hover:text-[#f5c842] transition-colors">
                      {BUSINESS.phone}
                    </p>
                  </div>
                </a>
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60 uppercase tracking-wider mb-1">Address</p>
                    <p className="text-white/90">{BUSINESS.address}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10">
                <a href={BUSINESS.phoneLink}>
                  <Button className="w-full bg-[#f5c842] hover:bg-[#e5b832] text-[#0d2137] font-bold py-4 rounded-xl uppercase tracking-wider" data-testid="contact-call-now">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <div className="map-container" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2361.8467091799446!2d-0.7689!3d53.8515!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48793c0f8f8f8f8f%3A0x48793c0f8f8f8f8f!2s5%20Houghton%20Ln%2C%20Sancton%2C%20York%20YO43%204QU%2C%20UK!5e0!3m2!1sen!2suk!4v1234567890123"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Tims Tilers Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer Section
function FooterSection() {
  return (
    <footer className="bg-[#0d2137] text-white pt-20 pb-8" data-testid="footer">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-20 mb-6 bg-white p-3 rounded-xl" />
            <p className="text-white/60 leading-relaxed max-w-md mb-6">
              Professional tiling services in East Yorkshire with over 25 years of experience. 
              Quality craftsmanship for kitchens, bathrooms and more.
            </p>
            <div className="flex gap-4">
              <a href={BUSINESS.facebook} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center hover:bg-[#1877f2] transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href={BUSINESS.phoneLink} className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center hover:bg-[#4a90c2] transition-colors">
                <Phone className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-wider mb-6"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Quick Links
            </h4>
            <nav className="space-y-3">
              {['About', 'Services', 'Showroom', 'Gallery', 'Contact'].map((link) => (
                <a
                  key={link}
                  href={`#${link.toLowerCase()}`}
                  className="block text-white/60 hover:text-[#f5c842] transition-colors font-medium"
                >
                  {link}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-wider mb-6"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Get In Touch
            </h4>
            <div className="space-y-4">
              <a 
                href={BUSINESS.phoneLink}
                className="flex items-center gap-3 text-white/60 hover:text-[#f5c842] transition-colors"
              >
                <Phone className="h-5 w-5 text-[#4a90c2]" />
                <span className="font-semibold text-white">{BUSINESS.phone}</span>
              </a>
              <div className="flex items-start gap-3 text-white/60">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#4a90c2]" />
                <span>{BUSINESS.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Tilers East Yorkshire | Bathroom Tiling | Kitchen Tiling
          </p>
        </div>
      </div>
    </footer>
  );
}

// Lightbox Component
function Lightbox({ image, onClose }) {
  if (!image) return null;
  
  return (
    <Dialog open={!!image} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
        <img 
          src={image} 
          alt="Gallery image"
          className="w-full h-auto"
          data-testid="lightbox-image"
        />
      </DialogContent>
    </Dialog>
  );
}

// Scroll to Top Button
function ScrollToTop({ visible }) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      className={`scroll-top-btn ${visible ? 'visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
      data-testid="scroll-to-top"
    >
      <ChevronUp className="h-6 w-6" />
    </button>
  );
}

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
    setShowScrollTop(window.scrollY > 500);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      
      <Navigation 
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <ShowroomSection />
        <GallerySection setLightboxImage={setLightboxImage} />
        <VideoSection />
        <WhyChooseUsSection />
        <ProcessSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      
      <FooterSection />
      
      <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      <ScrollToTop visible={showScrollTop} />
    </div>
  );
}

export default App;
